import { PIPELINES_AND_STEPS_PATHS } from 'constants/pipelines-and-steps-paths';
import {
    FieldMappingIcon,
    JavaScriptTransformIcon,
    RuleSetIcon,
} from '../../../assets';
import { CreateFeatureMenuItem } from '../../../components/create-feature-dialog/create-feature-dialog-helper';
import { alpha } from '@mui/system';
import { themeObject } from 'lib';
import {
    commonStyles,
    FIELD_MAPPING,
    JAVASCRIPT_TRANSFORM,
    RULE_SET,
} from '../pipelines-helper';
import {
    HeaderButtonConfig,
    TabItemsType,
} from 'components/landing-page-wrapper/components/TabComponent';
import { Icon } from '@iconify/react';
import AddIcon from '@mui/icons-material/Add';
import { RuleStepResponse } from 'hooks';
import { SortableSearchListItem } from './components';
import { ListItemType } from './components/sortable-search-list-item-table-helper';
import { ItemType } from './components/sortable-search-list-item-helper';

export const DETAILS = 'Details';

export const DETAILS_TAB_INDEX = 0;
export const STEP_TYPE_TAB_INDEX = 1;
export const ADD_BTN_INDEX = 0;
export const TRIAL_RUN_BTN_INDEX = 1;

export const createStepStyles = {
    menuItemStyles: {
        height: '140px',
        '&:hover': {
            backgroundColor: alpha(themeObject.palette.grayScale[50], 1),
        },
    },
    listItemStyles: {
        textWrap: 'wrap',
        marginRight: '12px',
        '& .MuiListItemText-primary': {
            fontWeight: '500 !important',
            fontSize: '24px !important',
            lineHeight: '28.18px !important',
        },
    },
};

export const stepMenuItems: CreateFeatureMenuItem[] = [
    {
        title: FIELD_MAPPING,
        subtitle:
            'Create a Drools MVEL set of rules using a visual editor or rule editor. Includes data quality actions.',
        icon: FieldMappingIcon,
        navigationPath: `${PIPELINES_AND_STEPS_PATHS.CREATE_NEW_STEP_DETAILS}?option=${FIELD_MAPPING}`,
    },
    {
        title: JAVASCRIPT_TRANSFORM,
        subtitle:
            'Create a JavaScript mapping function that accepts an object for manipulation and returns object.',
        icon: JavaScriptTransformIcon,
        navigationPath: `${PIPELINES_AND_STEPS_PATHS.CREATE_NEW_STEP_DETAILS}?option=${JAVASCRIPT_TRANSFORM}`,
    },
    {
        title: RULE_SET,
        subtitle:
            'Create a simplified mapping of inbound attribute name to new names for storing within a chain.',
        icon: RuleSetIcon,
        navigationPath: `${PIPELINES_AND_STEPS_PATHS.CREATE_NEW_STEP_DETAILS}?option=${RULE_SET}`,
    },
];

export const updateStepStyles = {
    newFieldMappingHeaderButtonWidth: {
        width: '216px',
    },
    newRulSetHeaderButtonWidth: {
        width: '135px',
    },
    trialRunHeaderButtonWidth: {
        width: '137px',
    },
    validateHeaderButtonWidth: {
        width: '140px',
    },
    jsTransformConatiner: {
        '& .MuiBox-root': {
            mx: 0,
        },
    },
    emptyFieldMappingContainer: {
        pt: 2,
        m: 'auto',
    },
    emptyStateContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: 2,
    },
    errorIconStyles: {
        height: '84px',
        width: '84px',
        color: themeObject.palette.tertiary.red[300],
    },
    emptyHeadingText: {
        fontFamily: 'Raleway',
        fontSize: '24px',
        fontWeight: 500,
        lineHeight: '28px',
        color: themeObject.palette.primary[700],
    },
    emptyBodyText: {
        fontFamily: 'Red Hat Text',
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: '21px',
        color: themeObject.palette.grayScale[700],
        textAlign: 'center',
        width: '412px',
    },
    trialRunAccordionContainer: {
        px: 3.8125,
        mb: 3,
        maxHeight: 'calc(100% - 53px)',
        overflowY: 'scroll',
        overflowX: 'hidden',
    },
};

export const transformFieldMappingInbounds = (
    map: Record<string, string>
): Array<Record<string, string>> => {
    const transformedData = Object.entries(map).map(([key, value]) => ({
        [key]: value || 'None',
    }));
    return transformedData;
};

export const transformRulesData = (
    data: RuleStepResponse[]
): Record<string, string>[] => {
    return data.map(({ name, description }) => ({
        name,
        description,
    }));
};

export const getInitialTabs = (
    option: string,
    readOnly: boolean,
    setDisplayPermissionDialog: React.Dispatch<React.SetStateAction<boolean>>
): TabItemsType[] => {
    let secondTabLabel = '';
    let secondTabHeading = option;
    let newButtonWidthStyle = {};
    let headerButtons: HeaderButtonConfig[] = [];

    if (option === JAVASCRIPT_TRANSFORM) {
        secondTabLabel = 'Transform';
        headerButtons = [
            {
                sx: {
                    ...commonStyles.addButtonStyles,
                    ...commonStyles.headerTabButtonStyles,
                    ...updateStepStyles.validateHeaderButtonWidth,
                },
                icon: (
                    <Icon
                        icon={'mdi:checkbox-marked-circle-outline'}
                        style={commonStyles.trialRunButtonIcon}
                    />
                ),
                label: 'Trial Run',
                onClick: (): void => {},
            },
        ];
    } else if (option === RULE_SET) {
        secondTabLabel = 'Rule Sets';
        secondTabHeading = 'Rule Sets';
        newButtonWidthStyle = updateStepStyles.newRulSetHeaderButtonWidth;
    } else {
        secondTabLabel = option;
        newButtonWidthStyle = updateStepStyles.newFieldMappingHeaderButtonWidth;
    }

    return [
        {
            label: DETAILS,
            tabHeaderConfig: { headingText: `${option} Details` },
        },
        {
            label: secondTabLabel,
            tabHeaderConfig: {
                headingText: secondTabHeading,
                headerButtonsConfig:
                    option === JAVASCRIPT_TRANSFORM
                        ? headerButtons
                        : [
                              {
                                  sx: {
                                      ...commonStyles.addButtonStyles,
                                      ...commonStyles.headerTabButtonStyles,
                                      ...newButtonWidthStyle,
                                  },
                                  icon: <AddIcon />,
                                  label:
                                      option === FIELD_MAPPING
                                          ? `New ${option}`
                                          : 'New Rule',
                                  onClick: (): void => {
                                      if (readOnly) {
                                          setDisplayPermissionDialog(true);
                                      }
                                  },
                              },
                              {
                                  sx: {
                                      ...commonStyles.addButtonStyles,
                                      ...commonStyles.headerTabButtonStyles,
                                      ...updateStepStyles.trialRunHeaderButtonWidth,
                                  },
                                  icon: (
                                      <Icon
                                          icon={
                                              'mdi:checkbox-marked-circle-outline'
                                          }
                                          style={
                                              commonStyles.trialRunButtonIcon
                                          }
                                      />
                                  ),
                                  label: 'Trial Run',
                                  onClick: (): void => {},
                              },
                          ],
            },
        },
    ];
};

export const transformToListItems = (
    listData: Array<Record<string, string>>,
    stepType: string
): ListItemType[] => {
    return listData.map((listItem, index) => {
        const key = Object.keys(listItem)[0];

        return {
            id: index,
            name: stepType === 'Field Mapping' ? key : listItem.name,
            item: (
                <SortableSearchListItem
                    data={listItem}
                    type={stepType as ItemType}
                    showItemMenu={false}
                />
            ),
        };
    });
};

export const getAccordionHeading = (stepType: string): string => {
    switch (stepType) {
        case JAVASCRIPT_TRANSFORM:
            return 'Transform';
        case FIELD_MAPPING:
            return 'Fields';
        case RULE_SET:
            return 'Rule Sets';
        default:
            return '';
    }
};
